import { SortChangedEvent } from "ag-grid-community";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { faUsers } from "@fortawesome/pro-solid-svg-icons";

import { type PYColumnDef, Pagination } from "@performyard/py-components";
import { ButtonIcon, Checkbox, InputText, Table, type TableRef, Text } from "@performyard/py-components";

import EmployeeSelectGroupsModal from "./components/EmployeeSelectGroupsModal";

import { useEmployeeSelectionObject } from "../../hooks/useEmployeeSelectionObject";

import { GetUserSelectionUsersProps, UserSelectionUsersResponse } from "../../api/services/defs/user-selection";

import AvatarGroup from "../AvatarGroup";
import ThumbSwitch from "../Inputs/ToggleSwitches/ThumbSwitch";

type EmployeeSelectionProps = {
	selectionObjectID: string;
	customFieldsIdentifiers: string[];
	usersToExcludeSelectionId?: string | null;
	exclusionList: string[];
	filters: { [key: string]: string | Array<string> };
	height?: string;
	paginationAlignment: "start" | "center" | "end";
	onlySelected?: boolean;
	onMutatingChange?: (isMutating: boolean) => void;
};

const DEFAULT_CUSTOM_FIELD_NAMES = ["Manager", "Group"];

const EmployeeSelection = ({
	selectionObjectID,
	customFieldsIdentifiers = DEFAULT_CUSTOM_FIELD_NAMES,
	usersToExcludeSelectionId = null,
	exclusionList = [],
	filters = {},
	height = "60dvh",
	paginationAlignment = "center",
	onlySelected = false,
	onMutatingChange,
}: EmployeeSelectionProps) => {
	const ref = useRef<TableRef<UserSelectionUsersResponse["users"][number]>>(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
	const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
	const lastValidPageCountRef = useRef(0);

	const [queryProps, setQueryProps] = useState<GetUserSelectionUsersProps>({
		selectionObjectID: selectionObjectID,
		page: 1,
		searchTerm: "",
		exclusionList: exclusionList,
		usersToExcludeSelectionId: usersToExcludeSelectionId ?? undefined,
		filters: filters,
		sortField: "name",
		sortDirection: "asc",
		limitSelected: false,
	});

	// Handle a changing seleciton object ID amongst the queries
	useEffect(() => {
		setQueryProps((prev) => ({
			...prev,
			selectionObjectID: selectionObjectID,
		}));
	}, [selectionObjectID]);

	// Update queryProps when debouncedSearchTerm changes
	useEffect(() => {
		setQueryProps((prev) => ({
			...prev,
			searchTerm: debouncedSearchTerm,
			page: 1, // Reset to first page when search changes
		}));
	}, [debouncedSearchTerm]);

	useEffect(() => {
		setQueryProps((prev) => ({
			...prev,
			limitSelected: onlySelected,
			page: 1,
		}));
	}, [onlySelected]);

	useEffect(() => {
		setQueryProps((prev) => ({
			...prev,
			exclusionList,
			usersToExcludeSelectionId: usersToExcludeSelectionId ?? undefined,
			page: 1,
		}));
	}, [exclusionList, usersToExcludeSelectionId]);

	const {
		data,
		usersList,
		isLoading,
		isFetching,
		addUsersToSelection,
		isAddingUsersToSelection,
		removeUsersFromSelection,
		isRemovingUsersFromSelection,
		count,
		initializedLoad,
	} = useEmployeeSelectionObject(queryProps);

	const isMutating = isLoading || isFetching || isAddingUsersToSelection || isRemovingUsersFromSelection;

	useEffect(() => {
		onMutatingChange?.(isMutating);
	}, [isMutating, onMutatingChange]);

	const selectedUsersCount = useMemo(() => {
		if (count && count > usersList?.length) {
			return usersList?.length;
		} else {
			return count;
		}
	}, [count, usersList]);

	const { selected_users = {}, departments = [], custom_fields = [], display_user_map = {} } = data || {};

	// Cache page_count to prevent jumping to 0 during refetches
	const effectivePageCount = useMemo(() => {
		if (data?.page_count && data.page_count > 0) {
			lastValidPageCountRef.current = data.page_count;
			return data.page_count;
		}
		return lastValidPageCountRef.current;
	}, [data?.page_count]);

	const userMapRef = useRef(display_user_map);
	const departmentsRef = useRef(departments);

	useEffect(() => {
		userMapRef.current = display_user_map;
		departmentsRef.current = departments;
	}, [display_user_map, departments, isLoading, isFetching]);

	const [selectedCustomFieldsColumns] = useMemo(() => {
		const fullCustomFieldList = custom_fields.map((field) => {
			return {
				headerName: field.name,
				_id: field._id,
				type: field.type,
			};
		});

		const groupsMap = departmentsRef.current.reduce((acc, department) => {
			acc[department._id] = department.name;
			return acc;
		}, {});
		const selectedCustomFieldsColumns = customFieldsIdentifiers
			.map((identifier) => {
				return fullCustomFieldList.find((field) => field.headerName === identifier || field._id === identifier);
			})
			.filter((field) => field !== undefined)
			.map((field) => {
				const { _id, headerName, type } = field;
				switch (type) {
					case "person":
						return {
							headerName: headerName,
							type: "employee",
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const manager = userMapRef.current[params.data.data_fields?.[_id]?.value];
								if (!manager) {
									return {
										_id: "",
										name: "",
									};
								}
								return {
									_id: params.data.data_fields?.[_id]?.value,
									name: manager?.name,
								};
							},
						};
					case "department":
						return {
							headerName: headerName,
							type: "text",
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const group = params.data.data_fields?.[_id]?.value;
								const groupName = groupsMap[group];
								if (!group) {
									return "";
								}

								return groupName;
							},
						};
					case "people":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							cellRenderer: (params) => {
								const peopleIds = params.data.data_fields?.[_id]?.value;
								if (!peopleIds || !Array.isArray(peopleIds)) {
									return null;
								}

								// Map people IDs to employee objects
								const employees = peopleIds
									.map((personId) => {
										const person = userMapRef.current[personId];
										return person
											? {
													_id: personId,
													name: person.name,
												}
											: null;
									})
									.filter((employee): employee is { _id: string; name: string } => employee !== null) // Remove null entries
									.slice(0, 5); // Cap at 5 as requested

								if (employees.length === 0) {
									return null;
								}

								return (
									<AvatarGroup
										employees={employees}
										maxLength={5}
										employeeNameTooltip={true}
										hasLinks={true}
										canShowModal={true}
									/>
								);
							},
						};
					case "date":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							type: "date",
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								return value || "";
							},
						};
					case "salary":
					case "monetary":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								if (!value && value !== 0) return "";
								return new Intl.NumberFormat("en-US", {
									style: "currency",
									currency: "USD",
								}).format(Number(value));
							},
						};
					case "numeric":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								if (!value && value !== 0) return "";
								return new Intl.NumberFormat("en-US").format(Number(value));
							},
						};
					case "phone":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								return value || "";
							},
						};
					case "single-select":
					case "multi-select":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							valueGetter: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								if (!value) return "";
								return Array.isArray(value) ? value.join(", ") : value;
							},
						};
					case "textbox":
						return {
							headerName: headerName,
							flex: 1,
							sortable: true,
							colId: _id,
							cellRenderer: (params) => {
								const value = params.data.data_fields?.[_id]?.value;
								if (!value) return "";
								// Truncate long text and show tooltip on hover
								const truncated = value.length > 50 ? `${value.substring(0, 47)}...` : value;
								return (
									<span title={value} className="py-line-clamp-2">
										{truncated}
									</span>
								);
							},
						};
					default:
						return {
							headerName: headerName,
							type: "text",
							flex: 1,
							valueGetter: (params) => {
								const isArray = Array.isArray(params.data.data_fields?.[_id]?.value);
								if (isArray) {
									return params.data.data_fields?.[_id]?.value
										.map((value: any) => value.name)
										.join(", ");
								}
								return params.data.data_fields?.[_id]?.value || "";
							},
						};
				}
			});

		return [selectedCustomFieldsColumns as PYColumnDef<UserSelectionUsersResponse["users"][number]>[]];
	}, [initializedLoad, onlySelected]);

	// Header component for select all functionality
	const SelectAllHeaderComponent = useMemo(() => {
		const component = (params: any) => {
			const { selected_users, addUsersToSelection, removeUsersFromSelection, selectionObjectID } = params.context;
			const { users } = data || {};

			const allSelected = users && users.length > 0 && users.every((user) => selected_users[user._id]);

			return (
				<div className={`${isLoading || isFetching ? "opacity-0" : ""}`}>
					<Checkbox
						id="select-all-checkbox"
						checked={allSelected}
						disabled={isLoading || isFetching}
						aria-label="Select all employees"
						onCheckedChange={(value) => {
							if (value) {
								addUsersToSelection({
									selectionObjectID: selectionObjectID,
									allUsers: true,
									...(exclusionList.length > 0 ? { exclusionList } : {}),
									...(usersToExcludeSelectionId ? { usersToExcludeSelectionId } : {}),
								});
							} else {
								removeUsersFromSelection({
									selectionObjectID: selectionObjectID,
									allUsers: true,
								});
							}
						}}
					/>
				</div>
			);
		};
		component.displayName = "SelectAllHeaderComponent";
		return component;
	}, [data, exclusionList, initializedLoad, isFetching, isLoading, usersToExcludeSelectionId]);

	// Create stable column definitions (no reactive dependencies)
	const columnDefs = useMemo<PYColumnDef<UserSelectionUsersResponse["users"][number]>[]>(() => {
		return [
			...(onlySelected
				? []
				: [
						{
							headerName: "",
							field: "select",
							width: 40,
							headerComponent: SelectAllHeaderComponent,
							cellRenderer: (params: any) => {
								// Access selection state and functions from context
								const {
									selected_users,
									addUsersToSelection,
									removeUsersFromSelection,
									selectionObjectID,
								} = params.context;

								return (
									<Checkbox
										size="small"
										id={`${params.data._id}-selection`}
										checked={!!selected_users[params.data._id]}
										onCheckedChange={(value) => {
											if (value) {
												addUsersToSelection({
													selectionObjectID: selectionObjectID,
													users: [params.data._id],
												});
											} else {
												removeUsersFromSelection({
													selectionObjectID: selectionObjectID,
													users: [params.data._id],
												});
											}
										}}
									/>
								);
							},
						},
					]),
			{
				headerName: "Name",
				sortable: true,
				colId: "name",
				type: "employee",
				flex: 1,
				valueGetter: (params) => {
					return {
						_id: params.data?._id || "",
						name: params.data?.name || "",
					};
				},
			},
			...selectedCustomFieldsColumns,
		];
	}, [selectedCustomFieldsColumns, SelectAllHeaderComponent]); // Only depends on column structure

	// Create context object that updates when selection state changes
	const gridContext = useMemo(
		() => ({
			selected_users,
			addUsersToSelection,
			removeUsersFromSelection,
			selectionObjectID,
		}),
		[selected_users, addUsersToSelection, removeUsersFromSelection, selectionObjectID]
	);

	// Refresh checkbox cells and header when selection state changes
	useEffect(() => {
		if (ref.current) {
			ref.current.api?.refreshCells({
				columns: ["select"], // Only refresh the checkbox column
				force: true,
			});
			ref.current.api?.refreshHeader(); // Refresh header to update select-all state
		}
	}, [selected_users]);

	const onSortChanged = (sort: SortChangedEvent) => {
		if (sort.columns && sort.columns[0]) {
			setQueryProps({
				...queryProps,
				sortField: sort.columns[0].getColId(),
				sortDirection: sort.columns[0].getSort() as string | undefined,
			});
		} else {
			setQueryProps({ ...queryProps, sortField: undefined, sortDirection: undefined });
		}
	};

	const alignmentClass = {
		start: "justify-start",
		center: "justify-center",
		end: "justify-end",
	}[paginationAlignment];

	return (
		<div>
			<div id="header row" className="flex flex-wrap items-center justify-between gap-2">
				<div id="header row left" className="flex gap-2">
					<div className="w-72">
						<InputText
							value={searchTerm}
							onChange={(value) => setSearchTerm(value)}
							leftIcon={faMagnifyingGlass}
							placeholder="Search employees..."
							clearable={searchTerm.length > 0}
							aria-label="Search employees"
						/>
					</div>
					<ButtonIcon
						size={32}
						variant="outline"
						icon={faUsers}
						onClick={() => setIsGroupsModalOpen(true)}
						disabled={isLoading}
						aria-label="Open groups selection modal"
					/>
				</div>

				{!onlySelected && (
					<div id="header row right" className="flex items-center gap-2">
						<Text size="paragraph-3" weight="regular" color="neutral-6">
							<span className="text-paragraph-3 font-semi-bold text-neutral-9">Show only selected</span> (
							{selectedUsersCount} users)
						</Text>
						<ThumbSwitch
							value={queryProps.limitSelected || false}
							onChange={(value) => setQueryProps({ ...queryProps, limitSelected: value, page: 1 })}
						/>
					</div>
				)}
			</div>
			<div id="selectionTable" className="mt-4 size-full">
				<div className="w-full" style={{ height: height }}>
					<Table
						columnDefs={columnDefs}
						rowData={usersList}
						ref={ref}
						loading={isLoading || isFetching}
						context={gridContext}
						onSortChanged={onSortChanged}
						emptyStateText="No employees found"
					/>
				</div>
				<div className={`flex ${alignmentClass}`}>
					<div className={`my-2 flex w-full max-w-[280px] ${alignmentClass}`}>
						<Pagination
							pageCount={effectivePageCount || 1}
							currentPage={queryProps.page || 1}
							onPageChange={(page) => setQueryProps({ ...queryProps, page })}
							dangerouslySetClassName={alignmentClass}
						/>
					</div>
				</div>
			</div>
			<EmployeeSelectGroupsModal
				isOpen={isGroupsModalOpen}
				handleClose={() => setIsGroupsModalOpen(false)}
				queryProps={queryProps}
			/>
		</div>
	);
};

EmployeeSelection.displayName = "EmployeeSelection";

export default EmployeeSelection;
